import { rainbowColors } from "@/pages/Dashboard/ComparisonTools";
import { colorSchemaList } from "@/utils/colorSchemas";
import { chartJsBorderDashMap, chartJsMarkerStyleMap, drawChartJsImage } from "@/utils/globalUtils";
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  LinearScale,
  TimeScale,
  // Filler,
} from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";
import moment from "moment";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { Line } from "react-chartjs-2";
import "chartjs-adapter-moment"; // if not already imported
import zoomPlugin from "chartjs-plugin-zoom";
import { VscRefresh } from "react-icons/vsc";

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, TimeScale, Title, Tooltip, Legend, annotationPlugin, zoomPlugin);

const hexToRgb = (hex) => {
  const bigint = parseInt(hex.replace("#", ""), 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `${r}, ${g}, ${b}`;
};

const alignItems = {
  left: "start",
  center: "center",
  right: "end",
};


const ChartJsLineChart = ({
  data = [],
  colorSchema,
  dateFormat = "D/M/YY hh:mm a",
  lineDashType,
  markerType,
  markerColor,
  gridLine = true,
  verticalLine = true,
  legendPosition = "center bottom",
  threshold = [],
  showThumbsUp = false,
  tickGaps = 2,
}) => {
  const chartRef = useRef(null);
  const chartContainerRef = useRef(null);

  const [chartData, setChartData] = useState({ datasets: [] });

  const annotations = threshold.reduce((acc, { value, color }, index) => {
    if (value && color) {
      acc[`line${index}`] = {
        type: "line",
        yMin: value,
        yMax: value,
        borderColor: color,
        borderWidth: 2,
      };
    }
    return acc;
  }, {});

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 50,
      },
    },
    scales: {
      x: {
        position: "bottom",
        grid: {
          display: false,
        },
        border: {
          display: verticalLine,
        },

        ticks: {
          callback: function (value, index) {
            const label = this.getLabelForValue(value);
            if (typeof Number(tickGaps) === "number" && tickGaps > 0) {
              return index % tickGaps === 0 ? moment(label, "DD/MM/YYYY HH:mm a").format(dateFormat) : undefined;
            }
            return index % 2 === 0 ? moment(label, "DD/MM/YYYY HH:mm a").format(dateFormat) : undefined;
          },
          major: { enabled: true },
          autoSkip: true,
          // minRotation: -25,
          // maxRotation: -25,
        },
        major: {
          enabled: true,
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          display: gridLine,
        },
        border: {
          display: verticalLine,
        },
      },
    },
    plugins: {
      annotation: { annotations },
      legend: {
        align: alignItems[legendPosition.split(" ")[0]] || "center",
        position: legendPosition.split(" ")[1] || "bottom",
      },
      zoom: {
        pan: {
          enabled: true,
          mode: "x", // x, y, or xy
        },
        zoom: {
          // wheel: {
          //   enabled: true,
          // },
          drag: {
            enabled: true,
            backgroundColor: "rgba(0,0,0,0.1)",
            borderColor: "#000",
            borderWidth: 1,
          },
          pinch: {
            enabled: true,
          },
          mode: "x",
        },
      },
    },
    animation: {
      onComplete: function ({ chart }) {
        if (showThumbsUp) {
          drawChartJsImage(chart);
        }
      },
      afterDraw: function ({ chart }) {
        if (showThumbsUp) {
          drawChartJsImage(chart);
        }
      },
    },
  };

  // effect
  useEffect(() => {
    const scheme = colorSchemaList.others?.find((item) => item?.value === colorSchema);
    const colors = scheme?.colors || rainbowColors;

    const chartInstance = chartRef.current;
    if (chartInstance && Array.isArray(data)) {
      const ctx = chartInstance.ctx;

      // 1. Extract all x values from all datasets, flatten and get unique
      let allXValues = data.flatMap(({ chart }) => (chart ? chart.map((item) => moment(item.x).format("DD/MM/YYYY HH:mm a")) : []));
      allXValues = [...new Set(allXValues)]; // unique
      allXValues.sort((a, b) => moment(a, "DD/MM/YYYY HH:mm a").diff(moment(b, "DD/MM/YYYY HH:mm a")));

      // 2. Build datasets with points aligned to allXValues
      const datasets = [...data]
        .sort((a, b) => (b?.chart?.length || 0) - (a?.chart?.length || 0))
        .map(({ chart, name, _id, color }, index) => {
          const gradient = ctx.createLinearGradient(0, 0, 0, 400);
          const baseColor = color || colors[index % colors.length];

          gradient.addColorStop(0, `rgba(${hexToRgb(baseColor)}, 0.2)`);
          gradient.addColorStop(1, `rgba(${hexToRgb(baseColor)}, 1)`);

          // Create a map for quick lookup
          const pointMap = {};
          if (Array.isArray(chart)) {
            chart.forEach((item) => {
              pointMap[moment(item.x).format("DD/MM/YYYY HH:mm a")] = item.y;
            });
          }

          // Align data to allXValues: if no data for x, use null (gap in line)
          const alignedData = allXValues.map((xVal) =>
            // eslint-disable-next-line no-prototype-builtins
            pointMap?.hasOwnProperty(xVal) ? pointMap[xVal] : null,
          );

          // Return dataset
          return {
            label: name,
            data: alignedData,
            _id,
            backgroundColor: gradient,
            borderColor: baseColor,
            borderWidth: 2,
            order: 2,
            // fill: true,
            // cubicInterpolationMode: "monotone",
            tension: 0.4,
            borderDash: chartJsBorderDashMap[lineDashType] || [],
            pointStyle: chartJsMarkerStyleMap[markerType] ?? "circle",
            pointBackgroundColor: markerColor,
            pointBorderColor: markerColor,
          };
        });

      setChartData({ labels: allXValues, datasets });
    }
  }, [data, colorSchema, lineDashType, markerType, markerColor]);

  // handler

  const handleResetZoom = () => {
    chartRef.current.resetZoom();
  };

  return (
    <>
      <div ref={chartContainerRef} style={{ position: "relative", width: "100%", height: "calc(100%  - 30px)" }}>
        <div className="position-absolute r-0 top-0 border border-primary px-3 cursor-pointer layout-controls" onClick={handleResetZoom}>
          <VscRefresh
            style={{
              fontWidth: "bold",
              fontSize: "20",
            }}
          />
        </div>
        <Line ref={chartRef} data={chartData} options={options} />
      </div>
    </>
  );
};

export default ChartJsLineChart;

ChartJsLineChart.propTypes = {
  data: PropTypes.array.isRequired,
  colorSchema: PropTypes.string,
  dateFormat: PropTypes.string,
  lineDashType: PropTypes.oneOf(["dot", "dash", "solid"]),
  markerType: PropTypes.oneOf(["square", "triangle", "cross", "none", "circle"]),
  markerColor: PropTypes.string,
  gridLine: PropTypes.bool,
  verticalLine: PropTypes.bool,
  legendPosition: PropTypes.string,
  threshold: PropTypes.array,
  showThumbsUp: PropTypes.bool,
  tickGaps: PropTypes.number,
};
